import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import Navbar from '@/components/Navbar';
import MetalTicker from '@/components/MetalTicker';
import Footer from '@/components/Footer';
import { getCollectibles, getCurrentRates, getPortfolioProfit } from '@/lib/api';
import { useAuth } from '@/lib/AuthContext';
import { ArrowRight, Sparkles, TrendingUp, TrendingDown, Plus, Minus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { formatSilverPurity } from '@/lib/productFormatting';
import { formatInr, MONEY_CLASSNAME } from '@/lib/money';

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.7, ease: [0.22, 1, 0.36, 1] } }
};
const stagger = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.08 } }
};

// Product card component matching reference design
function ProductCard({ item, ratesAvailable }) {
  const totalPrice = item.display_price;
  const priceMessage = item.price_status === 'confirmation_required' ? 'Price confirmation required' : 'Live rate temporarily unavailable';

  return (
    <Link to={`/collectible/${item.collectible_id}`} className="group" data-testid={`product-card-${item.collectible_id}`}>
      <motion.div variants={fadeUp} className="bg-card border border-border/30 rounded-2xl overflow-hidden hover:border-gold/30 transition-all duration-300">
        <div className="aspect-square bg-gradient-to-br from-secondary/50 to-secondary overflow-hidden relative">
          <img
            src={item.image_url || item.images?.[0] || 'https://images.unsplash.com/photo-1707569590646-2a51751bb9ef?w=600'}
            alt={item.name}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
          />
        </div>
        <div className="p-4">
          <h3 className="font-display text-base font-bold mb-1 group-hover:text-gold transition-colors">
            {item.name}
          </h3>
          <p className="text-xs text-muted-foreground mb-2" data-testid={`product-purity-${item.collectible_id}`}>
            {item.metal_type === 'gold'
              ? `${item.purity_karat || '24K'} Gold`
              : formatSilverPurity(item)}
          </p>
          <p className="text-xs text-muted-foreground mb-3">{item.weight}g</p>
          <div className="flex items-baseline justify-between">
            <p className={`${MONEY_CLASSNAME} text-xl font-semibold text-gold`} data-testid={`product-price-${item.collectible_id}`}>
              {formatInr(totalPrice) || priceMessage}
            </p>
            {ratesAvailable && item.metal_type === 'gold' && <span className="text-[10px] text-muted-foreground">Live Gold Linked</span>}
          </div>
        </div>
      </motion.div>
    </Link>
  );
}

export default function HomePage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [collectibles, setCollectibles] = useState([]);
  const [rates, setRates] = useState(null);
  const [portfolio, setPortfolio] = useState(null);
  const [activeCategory, setActiveCategory] = useState('All');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      getCollectibles().then(r => r.data || []),
      getCurrentRates().then(r => r.data).catch(() => null),
    ]).then(([items, ratesData]) => {
      setCollectibles(items);
      setRates(ratesData);
    }).catch(() => {}).finally(() => setLoading(false));

    // Load portfolio if user is logged in
    if (user) {
      getPortfolioProfit().then(r => setPortfolio(r.data)).catch(() => {});
    }
  }, [user]);

  const activeCollectibles = collectibles.filter(c =>
    c.quantity_remaining > 0
    && c.is_active !== false
    && c.is_archived !== true
    && c.archived !== true
    && c.status !== 'archived'
    && !c.deleted_at
  );
  const gold = activeCollectibles.filter(c => c.metal_type === 'gold');
  const silver = activeCollectibles.filter(c => c.metal_type === 'silver');
  const goldRate = rates?.gold?.rate_per_gram || 0;
  const silverRate = rates?.silver?.rate_per_gram || 0;
  const ratesAvailable = goldRate > 0 && silverRate > 0;

  // Get unique categories that have items
  const getAvailableCategories = (items) => {
    const cats = new Set();
    items.forEach(item => {
      const cat = item.category || item.design_type;
      if (cat) cats.add(cat.toLowerCase());
    });
    return Array.from(cats);
  };

  const goldCategories = getAvailableCategories(gold);
  const allCategories = ['All', ...goldCategories.map(c => c.charAt(0).toUpperCase() + c.slice(1))];

  // Filter gold items by category
  const filteredGold = activeCategory === 'All' 
    ? gold 
    : gold.filter(c => {
        const cat = (c.category || c.design_type || '').toLowerCase();
        return cat === activeCategory.toLowerCase();
      });

  const portfolioValue = portfolio?.summary?.total_current_value || 0;
  const portfolioChange = portfolio?.summary?.total_profit_loss || 0;

  return (
    <div className="min-h-screen bg-background" data-testid="home-page">
      <Navbar />
      <MetalTicker rates={rates} />
      <div className="grain-overlay" />

      {/* ===== HERO - Molten Gold Style ===== */}
      <section className="relative overflow-hidden min-h-[70vh] md:min-h-[80vh]" data-testid="hero-section">
        {/* Background Image - Molten Gold Effect */}
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1643324895873-a186cb9acab2?w=1800&q=80"
            alt="Molten gold texture"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-background via-background/80 to-transparent" />
          <div className="absolute inset-0 bg-gradient-to-t from-background via-transparent to-background/30" />
        </div>

        <div className="relative max-w-7xl mx-auto px-5 md:px-8">
          <div className="min-h-[70vh] md:min-h-[80vh] grid md:grid-cols-5 gap-8 items-center py-16">
            {/* Left Side - Tagline */}
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
              className="md:col-span-3"
            >
              <h1 className="font-display text-5xl sm:text-6xl lg:text-8xl font-extrabold tracking-tighter leading-[0.9] mb-8">
                Own <span className="text-gold">Gold.</span>
                <br />
                Wear Legacy.
              </h1>
              <p className="text-base md:text-lg text-foreground/50 leading-relaxed max-w-lg mb-10">
                Handcrafted gold & silver pieces with complete price transparency. Live rate linked. No hidden markups.
              </p>
              <div className="flex items-center gap-4 flex-wrap">
                <Link to="/gold">
                  <Button className="bg-gold text-background px-8 py-6 rounded-full text-sm font-bold uppercase tracking-widest hover:bg-gold-light" data-testid="hero-shop-gold">
                    Shop Gold
                  </Button>
                </Link>
                <Link to="/silver">
                  <Button variant="outline" className="border-foreground/20 text-foreground/70 px-8 py-6 rounded-full text-sm font-bold uppercase tracking-widest hover:border-foreground/40" data-testid="hero-shop-silver">
                    Shop Silver
                  </Button>
                </Link>
              </div>
            </motion.div>

            {/* Right Side - Portfolio Widget */}
            <motion.div
              initial={{ opacity: 0, x: 40 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.9, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
              className="md:col-span-2"
            >
              <div className="bg-card/80 backdrop-blur-md border border-border/40 rounded-2xl p-6" data-testid="portfolio-widget">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-display text-sm font-semibold text-foreground/70">Track Your Gold Portfolio</h3>
                  <div className="w-8 h-8 rounded-full bg-gold/10 flex items-center justify-center">
                    <TrendingUp className="w-4 h-4 text-gold" />
                  </div>
                </div>
                
                {user ? (
                  <>
                    <p className="font-display text-3xl md:text-4xl font-bold text-gold mb-1">
                      {formatInr(portfolioValue)}
                    </p>
                    <p className={`text-sm flex items-center gap-1 mb-6 ${portfolioChange >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                      {portfolioChange >= 0 ? <TrendingUp className="w-3.5 h-3.5" /> : <TrendingDown className="w-3.5 h-3.5" />}
                      {formatInr(Math.abs(portfolioChange))} today
                    </p>
                    <div className="flex gap-2">
                      <Button onClick={() => navigate('/gold')} className="flex-1 bg-gold/10 text-gold hover:bg-gold/20 border-0">
                        <Plus className="w-4 h-4 mr-1" /> Add
                      </Button>
                      <Button onClick={() => navigate('/vault')} variant="outline" className="flex-1 border-border/40">
                        View Vault
                      </Button>
                    </div>
                  </>
                ) : (
                  <>
                    <p className="font-display text-2xl font-bold text-foreground/50 mb-2">Start Tracking</p>
                    <p className="text-sm text-muted-foreground mb-6">Sign in to track your gold portfolio value in real-time</p>
                    <Button onClick={() => navigate('/login')} className="w-full bg-gold text-background hover:bg-gold/90">
                      Get Started
                    </Button>
                  </>
                )}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ===== CATEGORY FILTER BUTTONS ===== */}
      <section className="py-6 border-b border-border/20">
        <div className="max-w-7xl mx-auto px-5 md:px-8">
          <div className="flex items-center gap-2 overflow-x-auto pb-2 scrollbar-hide">
            {allCategories.map(cat => (
              <button
                key={cat}
                onClick={() => setActiveCategory(cat)}
                className={`px-5 py-2.5 rounded-full text-sm font-medium whitespace-nowrap transition-all duration-200 ${
                  activeCategory === cat
                    ? 'bg-gold text-background'
                    : 'bg-secondary/50 text-foreground/60 hover:bg-secondary hover:text-foreground'
                }`}
                data-testid={`category-${cat.toLowerCase()}`}
              >
                {cat}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* ===== GOLD COLLECTION ===== */}
      <section className="py-12 md:py-16" data-testid="gold-section">
        <div className="max-w-7xl mx-auto px-5 md:px-8">
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6">
            {filteredGold.slice(0, 4).map(item => (
              <ProductCard key={item.collectible_id} item={item} ratesAvailable={ratesAvailable} />
            ))}
          </div>

          {filteredGold.length > 4 && (
            <div className="text-center mt-8">
              <Link to="/gold">
                <Button variant="outline" className="border-gold/30 text-gold hover:bg-gold/10">
                  View All Gold Pieces <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
            </div>
          )}
        </div>
      </section>

      {/* ===== SILVER COLLECTION ===== */}
      <section className="py-12 md:py-16 bg-gradient-to-b from-white/[0.02] to-transparent" data-testid="silver-section">
        <div className="max-w-7xl mx-auto px-5 md:px-8">
          <motion.div
            initial="hidden" whileInView="visible" viewport={{ once: true, margin: '-80px' }} variants={fadeUp}
            className="flex items-end justify-between mb-8"
          >
            <div>
              <p className="text-[11px] uppercase tracking-[0.3em] text-silver/60 mb-3 font-medium">Silver Collection</p>
              <h2 className="font-display text-2xl md:text-4xl font-bold tracking-tight">Silver Pieces</h2>
            </div>
            <Link to="/silver" className="hidden md:flex items-center gap-2 text-sm font-medium text-foreground/40 hover:text-silver transition-colors group">
              View All <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
          </motion.div>
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, margin: '-80px' }} variants={stagger}
            className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6"
          >
            {silver.slice(0, 4).map(item => (
              <ProductCard key={item.collectible_id} item={item} ratesAvailable={ratesAvailable} />
            ))}
          </motion.div>
          <Link to="/silver" className="md:hidden flex items-center justify-center gap-2 mt-8 text-sm font-medium text-foreground/40 hover:text-silver transition-colors">
            View All Silver <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>

      {/* ===== CUSTOM JEWELRY CTA ===== */}
      <section className="py-16 md:py-24 relative overflow-hidden" data-testid="custom-cta-section">
        <div className="absolute inset-0 bg-gradient-to-br from-[#0a0a0a] via-background to-[#0a0a0a]" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] rounded-full bg-gold/5 blur-[150px]" />
        <div className="relative max-w-7xl mx-auto px-5 md:px-8">
          <motion.div
            initial="hidden" whileInView="visible" viewport={{ once: true, margin: '-80px' }} variants={fadeUp}
            className="grid md:grid-cols-2 gap-12 md:gap-16 items-center"
          >
            <div className="aspect-[4/5] rounded-3xl overflow-hidden relative">
              <img
                src="https://images.unsplash.com/photo-1581090465980-58ea88b43443?w=800&q=80"
                alt="Artisan crafting"
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
            </div>
            <div>
              <p className="text-[11px] uppercase tracking-[0.4em] text-gold mb-5 font-medium">The Atelier</p>
              <h2 className="font-display text-3xl md:text-5xl font-bold tracking-tight mb-6 leading-tight">
                Your Vision,
                <br />Our Craft.
              </h2>
              <p className="text-foreground/40 leading-relaxed mb-8 max-w-md text-base">
                Upload an image of any jewelry piece you love, and our artisans will handcraft it in pure gold or silver — tailored to your exact specifications.
              </p>
              <Link to="/custom">
                <Button className="bg-white text-black hover:bg-white/90 px-8 py-6 rounded-full" data-testid="custom-cta-button">
                  <Sparkles className="w-4 h-4 mr-2" /> Start Custom Order
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ===== BRAND PROMISE ===== */}
      <section className="py-16 md:py-24" data-testid="brand-story-section">
        <div className="max-w-7xl mx-auto px-5 md:px-8">
          <motion.div
            initial="hidden" whileInView="visible" viewport={{ once: true, margin: '-80px' }} variants={fadeUp}
            className="max-w-3xl mx-auto text-center"
          >
            <p className="text-[11px] uppercase tracking-[0.4em] text-foreground/30 mb-6 font-medium">Our Promise</p>
            <h2 className="font-display text-3xl md:text-4xl font-bold tracking-tight mb-6 leading-tight">
              Jewelry should be
              <br /><span className="text-gold">honest</span> & beautiful.
            </h2>
            <p className="text-base text-foreground/40 leading-relaxed max-w-xl mx-auto mb-12">
              Our price shows supplier cost, business costs, a 13% profit target on total cost, and GST. Supplier wastage stays specific to each product.
            </p>
            <div className="flex items-center justify-center gap-10 md:gap-16">
              {[
                { num: '999', label: 'Purity Standard' },
                { num: 'Au/Ag', label: 'Gold & Silver' },
                { num: '100%', label: 'Price Transparency' },
              ].map(stat => (
                <div key={stat.label} className="text-center">
                  <p className="font-display text-2xl md:text-3xl font-extrabold text-gold">{stat.num}</p>
                  <p className="text-[10px] text-foreground/30 mt-2 uppercase tracking-[0.2em]">{stat.label}</p>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
